import React, { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import PropTypes from 'prop-types'
import { Box, Button, CircularProgress, Paper, Stack, TextField, Typography } from '@mui/material'
import { PublicLink as Link } from '../components/PublicLink'
import { ImageLoader } from '../components/ImageLoader'
import Logo from '../icons/primusvest.png'
import { useUser } from '../hooks/useUser'
import { theme } from '../theme'
import { parseEmail, parsePassword } from '../utils/validation'
import { useForm } from 'react-hook-form'

const PasswordResetRequest = () => {
  const { requestPasswordReset } = useUser()
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState('info')
  const [loading, setLoading] = useState(false)
  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: {
      email: ''
    }
  })

  const onSubmit = async (data) => {
    setLoading(true)
    setMessage('')
    const result = await requestPasswordReset(data)
    setMessage(result.message)
    setMessageType(result.ok ? 'success' : 'error')
    setLoading(false)
  }

  return (
    <Paper elevation={8} sx={{ padding: '30px', width: { xs: 320, sm: 420 } }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant='h5' sx={{ textAlign: 'center', color: theme.palette.fontColor.main, fontSize: '22px', fontWeight: 700, marginBottom: '8px' }}>Resetare parolă</Typography>
          <Typography sx={{ textAlign: 'center', color: theme.palette.fontColor.medium, fontSize: '14px' }}>
            Introdu emailul contului și îți trimitem un link pentru setarea unei parole noi.
          </Typography>
        </Box>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2}>
            <TextField
              label='Email'
              size='small'
              type='email'
              {...register('email', {
                required: 'Câmp obligatoriu',
                validate: value => parseEmail(value) === true || parseEmail(value)
              })}
              error={!!errors.email}
              helperText={errors.email?.message}
            />
            <Button type='submit' variant='contained' color='accent' disabled={loading} sx={{ color: 'white', borderRadius: '21px' }}>
              {loading ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'Trimite link resetare'}
            </Button>
          </Stack>
        </form>
        {message && (
          <Typography sx={{ textAlign: 'center', fontSize: '14px', color: messageType === 'success' ? theme.palette.success.main : theme.palette.error.main }}>
            {message}
          </Typography>
        )}
        <Typography sx={{ textAlign: 'center', color: theme.palette.fontColor.main, fontSize: '14px' }}>
          V-ați amintit parola? <Link to='/login'><Typography variant='span' sx={{ color: theme.palette.accent.main, fontWeight: 600 }}>Înapoi la login</Typography></Link>
        </Typography>
      </Stack>
    </Paper>
  )
}

const PasswordResetConfirm = ({ token }) => {
  const { confirmPasswordReset } = useUser()
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState('info')
  const [loading, setLoading] = useState(false)
  const { register, handleSubmit, watch, setError, reset, formState: { errors } } = useForm({
    defaultValues: {
      password: '',
      repeat_password: ''
    }
  })
  const password = watch('password')

  const onSubmit = async (data) => {
    setMessage('')
    if (data.password !== data.repeat_password) {
      setError('repeat_password', {
        type: 'manual',
        message: 'Parolele trebuie să fie identice.'
      })
      return
    }

    setLoading(true)
    const result = await confirmPasswordReset({ token, password: data.password })
    setMessage(result.message)
    setMessageType(result.ok ? 'success' : 'error')
    if (result.ok) {
      reset()
    }
    setLoading(false)
  }

  return (
    <Paper elevation={8} sx={{ padding: '30px', width: { xs: 320, sm: 420 } }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant='h5' sx={{ textAlign: 'center', color: theme.palette.fontColor.main, fontSize: '22px', fontWeight: 700, marginBottom: '8px' }}>Setează parola nouă</Typography>
          <Typography sx={{ textAlign: 'center', color: theme.palette.fontColor.medium, fontSize: '14px' }}>
            Alege o parolă nouă pentru contul tău Primusvest.
          </Typography>
        </Box>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2}>
            <TextField
              label='Parola nouă'
              size='small'
              type='password'
              {...register('password', {
                required: 'Câmp obligatoriu',
                validate: value => parsePassword(value) === true || parsePassword(value)
              })}
              error={!!errors.password}
              helperText={errors.password?.message}
            />
            <TextField
              label='Repetă parola'
              size='small'
              type='password'
              {...register('repeat_password', {
                required: 'Câmp obligatoriu',
                validate: value => value === password || 'Parolele trebuie să fie identice.'
              })}
              error={!!errors.repeat_password}
              helperText={errors.repeat_password?.message}
            />
            <Button type='submit' variant='contained' color='accent' disabled={loading} sx={{ color: 'white', borderRadius: '21px' }}>
              {loading ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'Salvează parola nouă'}
            </Button>
          </Stack>
        </form>
        {message && (
          <Typography sx={{ textAlign: 'center', fontSize: '14px', color: messageType === 'success' ? theme.palette.success.main : theme.palette.error.main }}>
            {message}
          </Typography>
        )}
        <Typography sx={{ textAlign: 'center', color: theme.palette.fontColor.main, fontSize: '14px' }}>
          <Link to='/login'><Typography variant='span' sx={{ color: theme.palette.accent.main, fontWeight: 600 }}>Înapoi la login</Typography></Link>
        </Typography>
      </Stack>
    </Paper>
  )
}

PasswordResetConfirm.propTypes = {
  token: PropTypes.string.isRequired
}

const Parolanoua = () => {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') || ''

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', padding: '40px 0', bgcolor: theme.palette.secondary.main }}>
      <Stack spacing={1} alignItems='center'>
        <Link to='/' className='logo'>
          <Typography variant='h6' sx={{ display: 'flex', justifyContent: 'center', marginBottom: '40px' }}>
            <ImageLoader src={Logo} alt='Logo' style={{ width: '200px' }} loading='eager' />
          </Typography>
        </Link>
        {token ? <PasswordResetConfirm token={token} /> : <PasswordResetRequest />}
      </Stack>
    </Box>
  )
}

export default Parolanoua
